import {Helmet} from '@dr.pogodin/react-helmet';
import {closeCurrentDialog, openConfirmDialog, openDialog} from 'platform/components';
import {match} from 'ts-pattern';

import {isArray, noop} from 'ramda-adjunct';

import {
  usePatchTireManufacturerSetActiveMutation,
  usePatchTireManufacturerSetDefaultMutation,
  usePatchTireManufacturerSetInactiveMutation,
  usePatchTireManufacturerUnsetDefaultMutation,
} from '@dms/api/core';
import i18n from '@dms/i18n';
import {testIds} from '@dms/routes';
import {SettingsTemplate} from '@dms/settings/shared';
import {DmsDataGrid, handleApiError, refreshDmsDatagrid} from '@dms/shared';

import type {ActionCallback} from 'features/eag-grid';

import {BrandForm} from './components/BrandForm';

const PREDEFINED_COLUMNS = ['name', 'isActive', 'isDefault'] as const;

export function TireInventoryManufacturers() {
  const [patchTireManufacturerSetActive] = usePatchTireManufacturerSetActiveMutation();
  const [patchTireManufacturerSetDefault] = usePatchTireManufacturerSetDefaultMutation();
  const [patchTireManufacturerSetInactive] = usePatchTireManufacturerSetInactiveMutation();
  const [patchTireManufacturerUnsetDefault] = usePatchTireManufacturerUnsetDefaultMutation();

  const handleAddBrand = () => {
    openDialog(
      <BrandForm
        onDiscard={closeCurrentDialog}
        onSave={() => {
          closeCurrentDialog();
          refreshDmsDatagrid('tire-manufacturer');
        }}
        data-testid={testIds.settings.tireManufacturers('addBrandForm')}
      />,
      {
        title: i18n.t('entity.tiresInventory.labels.newBrand'),
        size: 'small',
      }
    );
  };

  const actionCallback: ActionCallback<typeof PREDEFINED_COLUMNS> = ({
    actionKey,
    rowId,
    getColumnData,
    refreshData,
  }) => {
    const id = isArray(rowId) ? rowId[0] : rowId;

    const name = getColumnData<string>('name')?.value ?? '';
    const isActive = getColumnData<boolean>('isActive')?.value ?? false;
    const isDefault = getColumnData<boolean>('isDefault')?.value ?? false;

    match(actionKey)
      .with('edit', 'redirectDetail', () =>
        openDialog(
          <BrandForm
            onDiscard={closeCurrentDialog}
            onSave={() => {
              closeCurrentDialog();
              refreshData();
            }}
            defaultValue={{
              id,
              name,
              isActive,
              isDefault,
            }}
            data-testid={testIds.settings.tireManufacturers('addBrandForm')}
          />,
          {
            title: i18n.t('entity.tiresInventory.labels.editBrand'),
            size: 'small',
          }
        )
      )
      .with('setInactive', () =>
        openConfirmDialog({
          text: i18n.t('entity.tiresInventory.labels.setInactive'),
          onConfirm: () =>
            patchTireManufacturerSetInactive({codelistId: id})
              .unwrap()
              .then(() => refreshData())
              .catch(handleApiError),
        })
      )
      .with('setActive', () =>
        openConfirmDialog({
          text: i18n.t('entity.tiresInventory.labels.setActive'),
          onConfirm: () =>
            patchTireManufacturerSetActive({codelistId: id})
              .unwrap()
              .then(() => refreshData())
              .catch(handleApiError),
        })
      )
      .with('setDefault', () =>
        openConfirmDialog({
          text: i18n.t('entity.tiresInventory.labels.setDefault'),
          onConfirm: () =>
            patchTireManufacturerSetDefault({codelistId: id})
              .unwrap()
              .then(() => refreshData())
              .catch(handleApiError),
        })
      )
      .with('unsetDefault', () =>
        openConfirmDialog({
          text: i18n.t('entity.tiresInventory.labels.unsetDefault'),
          onConfirm: () =>
            patchTireManufacturerUnsetDefault({codelistId: id})
              .unwrap()
              .then(() => refreshData())
              .catch(handleApiError),
        })
      )
      .otherwise(noop);
  };

  return (
    <>
      <Helmet title={i18n.t('entity.tiresInventory.labels.tireManufacturers')} />
      <SettingsTemplate
        header={{
          title: i18n.t('entity.tiresInventory.labels.tireManufacturers'),
          actions: [
            {
              type: 'button',
              title: i18n.t('entity.tiresInventory.labels.newBrand'),
              onClick: handleAddBrand,
            },
          ],
        }}
        description={i18n.t('entity.tiresInventory.labels.tireManufacturersDescription')}
        data-testid={testIds.settings.tireManufacturers('template')}
      >
        <DmsDataGrid
          gridCode="tire-manufacturer"
          actionCallback={actionCallback}
          predefinedColumns={PREDEFINED_COLUMNS}
          data-testid={testIds.settings.tireManufacturers('datagrid')}
        />
      </SettingsTemplate>
    </>
  );
}
